import cn from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmDataCreated } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';

import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmCollapse } from '../../MainModule/components/KnmCollapse';
import styles from './KnmViolatedRequirementsNpaItem.module.scss';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { IRequirementsNpaInitValues } from '../../RequirementsNpaModalModule/components/RequirementsNpaModal';
import { FormErrors } from '../../MainModule/types/FormErrors';
import { IKnmFormRef } from '../../MainModule/interfaces/IKnmFormRef';
import { KnmViolatedRequirementsMandatory } from './KnmViolatedRequirementsMandatory';
import { KnmViolatedRequirementsMandatoryAccordion } from './KnmViolatedRequirementsMandatoryAccordion';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';
import { useShowMore } from '../../../hooks/useShowMore';
import { Spinner } from '../../../UIPackage/MainModule/components/Spinner';
import {
  getKnmErknmIsPrescriptionFailed,
  getKnmErknmIsPrescriptionOldRules,
} from '../../../KnmErknmPackage/MainModule/selectors/knmErknmSelectors';
import {
  getDecisionIsPrescriptionFailed,
  getDecisionIsPrescriptionOldRules,
} from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';

interface IErknmViolatedRequirementsNpaItemProps {
  actRequirement?: IKnmErknmFormViolatedRequirementsNpa;
  addMandatory: (v: IRequirementsNpaInitValues, f: string) => void;
  error?: FormErrors<IKnmErknmFormViolatedRequirementsNpa>;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  id?: string;
  invalid?: boolean;
  isInsideDecisionsSection?: boolean;
  isInsideAgreementsSection?: boolean;
  isMandatoryDisabled: boolean;
  isViolationFieldsWatchOnly: boolean;
  name: string;
  onRemove: () => void;
  touched?: boolean;
  value: IKnmErknmFormViolatedRequirementsNpa;
  watchOnly: boolean;
  inAct?: boolean;
  isPaperless?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  signed?: boolean;
  onChangeFixPlanDate?: (date: string | Date) => void;
  isExpandAll?: boolean;
  extractVersion?: number;
}

interface IErknmViolatedRequirementsNpaItemStoreProps {
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
  isKnmPrescriptionOldRules: boolean | undefined;
  isKnmPrescriptionFailed: boolean | undefined;
  isDecisionPrescriptionOldRules: boolean | undefined;
  isDecisionPrescriptionFailed: boolean | undefined;
}

const KnmViolatedRequirementsNpaItemWithoutStore = ({
  actRequirement,
  addMandatory,
  error,
  formRef,
  id,
  invalid,
  isInsideDecisionsSection,
  isInsideAgreementsSection,
  isMandatoryDisabled,
  isViolationFieldsWatchOnly,
  name,
  onRemove,
  touched,
  value,
  watchOnly,
  inAct,
  isPaperless,
  onChangeFixPlanDate,
  isKnm,
  isPm,
  isDecision,
  signed,
  settings,
  created,
  isExpandAll,
  extractVersion,
  isKnmPrescriptionOldRules,
  isKnmPrescriptionFailed,
  isDecisionPrescriptionOldRules,
  isDecisionPrescriptionFailed,
}: IErknmViolatedRequirementsNpaItemProps & IErknmViolatedRequirementsNpaItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const mandatoryFieldName = `${name}.violatedMandatory`;
  const remove = formRef?.current.mutators.remove;

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  // @ts-ignore
  const reasonsErknm = formRef?.current.getFieldState(isDecision ? 'reasons' : 'reasonsErknm')?.value;

  const isPrescriptionCheckByOldRules = isDecision ? isDecisionPrescriptionOldRules : isKnmPrescriptionOldRules;
  const isPassportByCheckPrescriptionFailed = isDecision ? isDecisionPrescriptionFailed : isKnmPrescriptionFailed;
  const isPrescriptionCheck = reasonsErknm?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;

  const isDisabledByRepeatCheck =
    isEnablePrescriptionCheck &&
    (isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true) &&
    isPrescriptionCheckByOldRules === false;

  const handleAddMandatory = () => {
    addMandatory(
      {
        npa: value.npa,
        structuralUnit: value.structuralUnit,
      },
      mandatoryFieldName,
    );
  };

  const handleRemoveMandatory = (index: number) => {
    remove && remove(mandatoryFieldName, index);
  };

  const violatedMandatoryError = getFieldArrayErrorMessage(error?.violatedMandatory) || '';

  const npaDocumentName = useMemo(() => {
    if (value.npa.title?.toLocaleLowerCase().indexOf('приказ') !== -1) {
      return 'Приказ';
    }
    if (value.npa.title?.toLocaleLowerCase().indexOf('постановление') !== -1) {
      return 'Постановление';
    }
    if (value.npa.title?.toLocaleLowerCase().indexOf('указ') !== -1) {
      return 'Указ';
    }
    if (value.npa.title?.toLocaleLowerCase().indexOf('устав') !== -1) {
      return 'Устав';
    }
    if (value.npa.title?.toLocaleLowerCase().indexOf('кодекс') !== -1) {
      return 'Кодекс';
    }

    return 'Документ';
  }, [value]);

  const titleAccordions = useMemo(() => {
    if (isPaperless) {
      return (
        <>
          {npaDocumentName} №{value.npa.number}
          <span className={styles.CollapseTitleGrey}> от {value.npa.acceptanceDate}</span>
        </>
      );
    }

    return (
      <>
        <span className={styles.CollapseTitleGrey}>НПА №</span>
        {value.npa.number}
        <span className={styles.CollapseTitleGrey}>от</span>
        {value.npa.acceptanceDate}
      </>
    );
  }, [value]);

  const canRemove = useMemo(() => {
    const wasResolved = value.violatedMandatory?.find(
      m => !!m.violations?.find(v => v.status === ViolationStatusesEnum.FIXED),
    );

    return !watchOnly && !wasResolved;
  }, [watchOnly, value, formRef]);

  const hasError = checkHasErrorInCollapseBlock(formRef, mandatoryFieldName);

  return (
    <div
      className={cn(styles.CollapseBlock, {
        [styles.CollapseBlockError]: touched && (invalid || violatedMandatoryError),
      })}
    >
      <KnmCollapse
        theme="fields"
        title={titleAccordions}
        onClose={canRemove ? onRemove : undefined}
        id={id || undefined}
        openByDefault={value.isNew || isExpandAll}
        headerPostfix={isPaperless && value.isNew && <div className={styles.ActStatus}>Черновик</div>}
        error={isKnmCollapseOpened && hasError}
        changeOpenedCallback={setIsKnmCollapseOpened}
      >
        <>
          <div className={cn(styles.NpaName, { [styles.NpaNameNew]: isPaperless })}>{value.npa.title}</div>
          <div
            className={cn(styles.StructureUnitTitle, {
              [styles.StructureUnitTitleNew]: isPaperless,
            })}
          >
            {!isPaperless && <span>Структурная единица</span>}
            <span>{value.structuralUnit.title}</span>
          </div>
          <div className={styles.BeforeMandatoryBlock} id={`${id}.violatedMandatory`}>
            {getTitleFieldWithRedStar({
              label: 'Нарушенные обязательные требования',
              field: `${name}.violatedMandatory`,
              isPm,
              isKnm,
              isDecision,
              additionContent: !watchOnly && !isMandatoryDisabled && (
                <span className="shared-no-wrap-text">
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    onClick={handleAddMandatory}
                  />
                </span>
              ),
            })}
          </div>
          {touched && violatedMandatoryError && (
            <p className="shared-input-error shared-row__small-margin">{violatedMandatoryError}</p>
          )}
          <FieldArray name={mandatoryFieldName}>
            {({ fields }) => {
              // Пагинация для оптимизации рендеринга большого количества нарушений
              const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(
                fields.value,
                {
                  initialCount: 10,
                  incrementCount: 10,
                },
              );

              return (
                <>
                  {visibleItems.map((mandatoryValue: IKnmErknmFormViolatedRequirementsMandatory) => {
                    const index =
                      fields.value?.findIndex(
                        (item: IKnmErknmFormViolatedRequirementsMandatory) => item === mandatoryValue,
                      ) ?? 0;
                    const violatedMandatoryName = `${mandatoryFieldName}[${index}]`;
                    const correspondingActMandatoryViolations =
                      actRequirement?.violatedMandatory?.find(
                        mandatory => mandatory.mandatory.id === mandatoryValue.mandatory.id,
                      )?.violations || [];
                    const mId = `${id}.violatedMandatory[${index}]`;

                    if (isPaperless) {
                      return (
                        <KnmViolatedRequirementsMandatoryAccordion
                          name={name}
                          formRef={formRef}
                          isMandatoryDisabled={isMandatoryDisabled}
                          isViolationFieldsWatchOnly={isViolationFieldsWatchOnly}
                          watchOnly={watchOnly}
                          inAct={inAct}
                          isInsideDecisionsSection={isInsideDecisionsSection}
                          isInsideAgreementsSection={isInsideAgreementsSection}
                          key={violatedMandatoryName}
                          violatedMandatoryName={violatedMandatoryName}
                          mandatoryValue={mandatoryValue}
                          index={index}
                          handleRemoveMandatory={handleRemoveMandatory}
                          correspondingActMandatoryViolations={correspondingActMandatoryViolations}
                          isPaperless={isPaperless}
                          onChangeFixPlanDate={onChangeFixPlanDate}
                          mId={mId}
                          isKnm={isKnm}
                          isPm={isPm}
                          isDecision={isDecision}
                          touched={touched}
                          signed={signed}
                          isDisabledByRepeatCheck={isDisabledByRepeatCheck}
                          isExpandAll={isExpandAll}
                          extractVersion={extractVersion}
                        />
                      );
                    }

                    return (
                      <KnmViolatedRequirementsMandatory
                        name={name}
                        formRef={formRef}
                        isMandatoryDisabled={isMandatoryDisabled}
                        isViolationFieldsWatchOnly={isViolationFieldsWatchOnly}
                        watchOnly={watchOnly}
                        inAct={inAct}
                        isInsideDecisionsSection={isInsideDecisionsSection}
                        key={violatedMandatoryName}
                        violatedMandatoryName={violatedMandatoryName}
                        mandatoryValue={mandatoryValue}
                        index={index}
                        handleRemoveMandatory={handleRemoveMandatory}
                        correspondingActMandatoryViolations={correspondingActMandatoryViolations}
                        isPaperless={false}
                        mId={mId}
                        isKnm={isKnm}
                        isPm={isPm}
                        isDecision={isDecision}
                        disabledByRepeatCheck={isDisabledByRepeatCheck}
                        isExpandAll={isExpandAll}
                        extractVersion={extractVersion}
                      />
                    );
                  })}
                  {isRendering && <Spinner className={styles.Loader} />}
                  {hasMore && !isRendering && (
                    <div className="shared-row__small-margin">
                      <Button
                        text={`Показать еще (${visibleCount} из ${totalCount})`}
                        type="button"
                        variant="link"
                        color="primary"
                        onClick={showMore}
                      />
                    </div>
                  )}
                </>
              );
            }}
          </FieldArray>
        </>
      </KnmCollapse>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IErknmViolatedRequirementsNpaItemStoreProps => ({
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
  isKnmPrescriptionOldRules: getKnmErknmIsPrescriptionOldRules(state),
  isKnmPrescriptionFailed: getKnmErknmIsPrescriptionFailed(state),
  isDecisionPrescriptionOldRules: getDecisionIsPrescriptionOldRules(state),
  isDecisionPrescriptionFailed: getDecisionIsPrescriptionFailed(state),
});

export const KnmViolatedRequirementsNpaItem = connect(mapStateToProps)(KnmViolatedRequirementsNpaItemWithoutStore);
